import type { TextFormat } from './types';

export function safeTextLink(value: string): boolean {
  if (typeof value !== 'string' || value.length > 2000 || /[\u0000-\u0020\u007f]/.test(value)) return false;
  try { const url = new URL(value); return ['https:', 'http:', 'mailto:'].includes(url.protocol); } catch { return false; }
}

export function validTextFormat(value: unknown, text: string): value is TextFormat[] | undefined {
  if (value === undefined) return true;
  if (!Array.isArray(value) || value.length > 1000) return false;
  let end = 0;
  return value.every(range => {
    if (!range || typeof range !== 'object' || !Number.isInteger(range.start) || !Number.isInteger(range.end) || range.start < end || range.end <= range.start || range.end > text.length) return false;
    end = range.end;
    return (range.list === undefined || ['ordered', 'unordered'].includes(range.list)) && ['bold', 'italic', 'underline'].every(key => range[key] === undefined || typeof range[key] === 'boolean') && (range.link === undefined || safeTextLink(range.link));
  });
}

export function textSegments(text: string, format?: TextFormat[]) {
  const segments: { text: string; format?: TextFormat }[] = [];
  let offset = 0;
  if (validTextFormat(format, text)) for (const range of format ?? []) {
    if (range.start > offset) segments.push({ text: text.slice(offset, range.start) });
    segments.push({ text: text.slice(range.start, range.end), format: range });
    offset = range.end;
  }
  if (offset < text.length) segments.push({ text: text.slice(offset) });
  return segments;
}

/** Line groups keep list structure separate from inline styling. */
export function richTextBlocks(text: string, format?: TextFormat[]) {
  const ranges = validTextFormat(format, text) ? format ?? [] : [];
  const blocks: { list?: 'ordered' | 'unordered'; lines: { text: string; format?: TextFormat[] }[] }[] = [];
  let start = 0;
  const lines = text.split('\n');
  lines.forEach((line, index) => {
    const end = start + line.length;
    if (index === lines.length - 1 && !line && start > 0 && ranges.some(range => range.end === start && range.list)) return;
    const list = ranges.find(range => range.list && range.start < end + 1 && range.end > start)?.list;
    const inline = ranges.filter(range => range.start < end && range.end > start).map(range => ({ ...range, start: Math.max(range.start, start) - start, end: Math.min(range.end, end) - start }));
    const last = blocks.at(-1);
    const item = { text: line, format: inline.length ? inline : undefined };
    if (last && last.list === list) last.lines.push(item);
    else blocks.push({ list, lines: [item] });
    start = end + 1;
  });
  return blocks;
}
